import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { PlanService } from '../../../core/services/plan.service';
import { accesSatisfait } from '../../../core/services/acces-route';
import { PermissionService } from '../../security/services/permission.service';
import { BASE_CONFIG_FISCALE, ONGLETS_CONFIG_FISCALE, OngletConfigFiscale, SEGMENT_AUCUN_ACCES, contenuDisponible, lienOnglet } from './config-fiscale-onglets';

/**
 * Les onglets que l'utilisateur peut ouvrir : droit ET module, comme `permissionGuard` + `moduleGuard` sur la route
 * de chaque onglet. Sans plan actif (hors super-admin), aucun : c'est le refus de `moduleGuard` et du menu.
 * Un onglet dont seul le module du CONTENU manque (« Regles fiscales » sans taxEngine) est dans la liste : il est visible
 * et affiche un message (`contenuDisponible`).
 */
export function ongletsAccessibles(
  permissionService: Pick<PermissionService, 'hasPermission'> & { isSuperAdmin: () => boolean },
  planService: Pick<PlanService, 'checkModuleByName' | 'hasActivePlan'>
): OngletConfigFiscale[] {
  if (!permissionService.isSuperAdmin() && !planService.hasActivePlan()) return [];
  return ONGLETS_CONFIG_FISCALE.filter(o => accesSatisfait(o.acces, permissionService, planService));
}

/**
 * Garde de `/accounting/config` (onglet par defaut). La coquille n'exige aucun droit ; c'est ici qu'on envoie
 * l'utilisateur vers son premier onglet accessible quand « Configuration comptable » ne l'est pas, ou vers le message
 * « aucun acces » quand aucun ne l'est. Les gardes habituelles (`moduleGuard`, `permissionGuard`) suivent pour le cas
 * ou l'onglet par defaut est accessible.
 * On prefere un onglet dont le contenu est disponible : l'onglet qui n'affiche qu'un message « plan sans les regles
 * fiscales » n'est choisi que s'il est le SEUL accessible (le message est alors l'information utile).
 */
export const ongletParDefautGuard: CanActivateFn = () => {
  const router = inject(Router);
  const plan = inject(PlanService);
  const accessibles = ongletsAccessibles(inject(PermissionService), plan);
  if (accessibles.length === 0) return router.createUrlTree([BASE_CONFIG_FISCALE, SEGMENT_AUCUN_ACCES]);
  const premier = accessibles.find(o => contenuDisponible(o, plan)) ?? accessibles[0];
  if (premier.cle === 'configuration') return true;
  return router.parseUrl(lienOnglet(premier));
};
